// src/pages/calendar/calendarsearch.js
import React, { useEffect, useMemo, useRef, useState } from "react";

/** Firestore Timestamp | Date -> Date */
const toDate = (v) => (v?.toDate ? v.toDate() : new Date(v));

/** TR karakter normalizasyonu */
const norm = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/İ/g, "i")
    .toLowerCase()
    .trim();

/**
 * props:
 *  - meetings: [{ title, startDate, ... }]
 *  - onSelectDay: (date: Date) => void   // seçildiğinde o güne git
 */
export default function CalendarSearch({ meetings = [], onSelectDay }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  const results = useMemo(() => {
    const qq = norm(q);
    if (qq.length < 2) return [];
    const rows = [];

    for (const m of meetings) {
      if (!m?.startDate) continue;
      const title = (m.title || "(Başlıksız)").toString();
      if (!norm(title).includes(qq)) continue;
      const d = toDate(m.startDate);

      rows.push({
        id: m.meetingId || m.id,
        title,
        date: d,
        time: d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }),
      });
    }

    rows.sort((a, b) => a.date.getTime() - b.date.getTime());
    return rows.slice(0, 30);
  }, [q, meetings]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handlePick = (row) => {
    setOpen(false);
    setQ("");
    onSelectDay?.(new Date(row.date));
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[active]) handlePick(results[active]);
    } else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} className="position-relative" style={{ minWidth: 200 }}>
      <div className="input-group shadow-sm input-group-sm">
        <span className="input-group-text bg-white border-end-0">🔎</span>
        <input
          ref={inputRef}
          className="form-control border-start-0"
          type="search"
          placeholder="Ara"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => q.length >= 2 && setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <button
          className="btn btn-outline-primary"
          type="button"
          onClick={() => results[0] && handlePick(results[0])}
          title="Bul ve git"
        >
          Git
        </button>
      </div>

      {open && results.length > 0 && (
        <div className="dropdown-menu show w-100 mt-1 p-0 shadow" style={{ maxHeight: 360, overflowY: "auto", borderRadius: 8 }}>
          {results.map((r, idx) => {
            const isActive = idx === active;
            const dayText = r.date.toLocaleDateString("tr-TR", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            });
            return (
              <button
                key={`${r.id}-${idx}`}
                type="button"
                className={`dropdown-item d-flex align-items-start gap-2 ${isActive ? "active" : ""}`}
                onMouseEnter={() => setActive(idx)}
                onClick={() => handlePick(r)}
                style={{ whiteSpace: "normal", lineHeight: 1.25, padding: "10px 12px" }}
              >
                <div className="mt-1">📅</div>
                <div className="text-start">
                  <div className="fw-semibold">{r.title}</div>
                  <div className="small text-muted">
                    {dayText} • {r.time}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {open && q.length >= 2 && results.length === 0 && (
        <div className="dropdown-menu show w-100 mt-1 p-2 text-muted" style={{ borderRadius: 8 }}>
          Sonuç bulunamadı
        </div>
      )}
    </div>
  );
}
